/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/components/Topbar.jsx
 * Description  : Top header with profile dropdown
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const ROLE_LABELS = { admin: 'ผู้ดูแลระบบ', user: 'ผู้ใช้งาน', view: 'ดูอย่างเดียว' };

function initials(fname, lname) {
  return ((fname || '')[0] || '') + ((lname || '')[0] || '');
}

export default function Topbar({ pageTitles, pageSubtitles, autoMode, onToggleAutoMode, onRefresh, currentUser, onLogout }) {
  const PROFILE = currentUser || { fname: 'Admin', lname: '', email: '', role: 'admin' };
  const location = useLocation();
  const navigate = useNavigate();
  const title = pageTitles[location.pathname] || 'CSAF';
  const subtitle = pageSubtitles?.[location.pathname] || 'CSAF · Charoensin Alert Finance';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
      <div>
        <h1 className="text-base font-semibold text-gray-800 leading-tight">{title}</h1>
        <div className="text-xs text-gray-400 leading-tight">{subtitle}</div>
      </div>

      <div className="flex items-center gap-3">
        {/* Auto Mode chip */}
        <button
          onClick={onToggleAutoMode}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-200
            ${autoMode
              ? 'bg-green-50 text-success border-green-200 hover:bg-green-100'
              : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
            }`}
          title="คลิกเพื่อเปิด/ปิด Auto Mode"
        >
          <span className={`w-2 h-2 rounded-full ${autoMode ? 'bg-success' : 'bg-gray-400'}`} />
          Auto Mode
          <span className={`text-xs font-bold ${autoMode ? 'text-success' : 'text-gray-400'}`}>
            {autoMode ? 'ON' : 'OFF'}
          </span>
        </button>

        {/* Refresh button */}
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-600 text-sm hover:bg-gray-100 transition-colors"
          title="รีเฟรชข้อมูล"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          รีเฟรช
        </button>

        {/* Profile avatar + dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold shrink-0">
              {initials(PROFILE.fname, PROFILE.lname)}
            </div>
            <div className="text-left hidden sm:block">
              <div className="text-sm font-medium text-gray-800 leading-tight">{PROFILE.fname} {PROFILE.lname}</div>
              <div className="text-xs text-gray-400 leading-tight">{ROLE_LABELS[PROFILE.role]}</div>
            </div>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-60 bg-white border border-gray-100 rounded-xl shadow-lg z-50 overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {initials(PROFILE.fname, PROFILE.lname)}
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-800">{PROFILE.fname} {PROFILE.lname}</div>
                  <div className="text-xs text-gray-500">{PROFILE.email}</div>
                  <span className="inline-block mt-1 px-2 py-0.5 bg-primary text-white text-xs rounded-full font-medium">
                    {ROLE_LABELS[PROFILE.role]}
                  </span>
                </div>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => { setMenuOpen(false); navigate('/users'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  จัดการผู้ใช้งาน
                </button>
                <button
                  onClick={() => { setMenuOpen(false); navigate('/settings'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  ตั้งค่าระบบ
                </button>

                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-danger hover:bg-red-50 transition-colors text-left"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    ออกจากระบบ
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
